import { useEffect, useState } from "react";
import { RefreshCw, Coins, BarChart3, Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";

const num = "surface-2 border border-white/10 rounded-lg px-2 h-8 text-sm w-full outline-none focus:border-gold/50";

export function VirtuCityEconomyAdmin() {
  return (
    <>
      <a href="/vstreets/admin" className="block rounded-2xl border border-gold/30 p-4 text-sm font-semibold text-gold hover:bg-gold/5" data-testid="vs-admin-link">Open VSTREETS Control Center (crew approvals, territories, sync, audit) →</a>
      <RoleResyncCard />
      <EconomySettingsCard />
      <ChartWeeksCard />
      <PayoutsLogCard />
    </>
  );
}

function RoleResyncCard() {
  const [run, setRun] = useState(null);
  const load = () => api.get("/admin/virtucity/discord/resync").then(({ data }) => setRun(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [run?.status]);
  const start = async () => { try { const { data } = await api.post("/admin/virtucity/discord/resync"); setRun(data); toast.success("Resync started"); } catch (e) { toast.error(errText(e)); } };
  const st = run?.stats;
  return (
    <div className="surface rounded-2xl p-5" data-testid="vc-resync-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold flex items-center gap-2"><RefreshCw size={16} className="text-gold" />Discord role resync</div>
          <p className="text-xs text-muted-foreground mt-1">Re-checks every citizen: adds the Verified Citizen role where missing and removes it from anyone who isn't a citizen.</p>
        </div>
        <Button onClick={start} disabled={run?.status === "running"} className="btn-gold text-black rounded-full" data-testid="vc-resync-btn">
          {run?.status === "running" ? <><Loader2 size={14} className="animate-spin mr-1.5" />Resyncing…</> : "Resync roles now"}
        </Button>
      </div>
      {st && (
        <div className="mt-3 text-xs flex flex-wrap gap-2" data-testid="vc-resync-result">
          {[["Checked", st.checked], ["Added", st.added], ["Removed", st.removed], ["Not in server", st.not_in_server], ["Errors", st.errors]].map(([l, v]) => (
            <span key={l} className="surface-2 rounded-full px-3 h-7 flex items-center">{l}: <b className="ml-1">{v}</b></span>
          ))}
          <span className="text-muted-foreground self-center">{new Date(run.finished_at || run.started_at).toLocaleString()}</span>
          {!st.member_scan && <p className="w-full text-[11px] text-amber-400 mt-1">Only linked accounts were checked. To also remove the role from people who never linked, turn on <b>Server Members Intent</b> (Discord Developer Portal → Bot → Privileged Gateway Intents).</p>}
        </div>
      )}
    </div>
  );
}

function EconomySettingsCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/virtucity/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const setRow = (i, k, v) => setS({ ...s, chart_rewards: s.chart_rewards.map((r, j) => (j === i ? { ...r, [k]: Number(v) } : r)) });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/virtucity/settings", {
        payout_daily_cap_vc: Number(s.payout_daily_cap_vc), payouts_enabled: s.payouts_enabled,
        city_per_vc: Number(s.city_per_vc), withdraw_enabled: s.withdraw_enabled, withdraw_daily_cap_vc: Number(s.withdraw_daily_cap_vc),
        chart_rewards_enabled: s.chart_rewards_enabled, weekly_post_enabled: s.weekly_post_enabled, chart_rewards: s.chart_rewards,
      });
      setS(data); toast.success("VirtuCity economy saved");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const Toggle = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} data-testid={`vc-set-${k}`} className="accent-[#D4AF37]" />{label}</label>
  );
  return (
    <div className="surface rounded-2xl p-5" data-testid="vc-economy-card">
      <div className="font-display font-semibold flex items-center gap-2 mb-1"><Coins size={16} className="text-gold" />City payouts & chart rewards</div>
      <p className="text-xs text-muted-foreground mb-4">Rate: {Number(s.city_per_vc).toLocaleString()} city money = 1 VC. Only VirtuStars+ members get payouts.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Toggle k="payouts_enabled" label="City payouts enabled" />
          <Toggle k="withdraw_enabled" label="VirtuStars+ city bank withdrawals (promo VC)" />
          <label className="text-xs text-muted-foreground block">City money per 1 VC
            <input type="number" min="100" value={s.city_per_vc} onChange={(e) => setS({ ...s, city_per_vc: e.target.value })} className={`${num} mt-1 max-w-[180px]`} data-testid="vc-set-rate" /></label>
          <label className="text-xs text-muted-foreground block">Withdrawal daily cap (promo VC)
            <input type="number" min="0" value={s.withdraw_daily_cap_vc} onChange={(e) => setS({ ...s, withdraw_daily_cap_vc: e.target.value })} className={`${num} mt-1 max-w-[140px]`} data-testid="vc-set-withdraw-cap" /></label>
          <label className="block text-xs text-muted-foreground">Daily cap per citizen (VC)
            <input type="number" min="0" step="0.5" value={s.payout_daily_cap_vc} onChange={(e) => setS({ ...s, payout_daily_cap_vc: e.target.value })} className={`${num} mt-1 max-w-[140px]`} data-testid="vc-set-daily-cap" />
          </label>
          <Toggle k="chart_rewards_enabled" label="Pay weekly City Chart top-10 rewards" />
          <Toggle k="weekly_post_enabled" label="Post the top 10 to Discord every Monday" />
        </div>
        <div>
          <div className="grid grid-cols-[40px_1fr_1fr] gap-2 text-[11px] text-muted-foreground mb-1"><span>Rank</span><span>VC</span><span>Fame</span></div>
          <div className="space-y-1.5">
            {s.chart_rewards.map((r, i) => (
              <div key={r.rank} className="grid grid-cols-[40px_1fr_1fr] gap-2 items-center">
                <span className="text-sm font-display font-bold text-gold">#{r.rank}</span>
                <input type="number" min="0" value={r.vc} onChange={(e) => setRow(i, "vc", e.target.value)} className={num} data-testid={`vc-reward-vc-${r.rank}`} />
                <input type="number" min="0" value={r.fame} onChange={(e) => setRow(i, "fame", e.target.value)} className={num} data-testid={`vc-reward-fame-${r.rank}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="btn-gold text-black rounded-full mt-4" data-testid="vc-economy-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" />Save</>}</Button>
    </div>
  );
}

function ChartWeeksCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/virtucity/charts/weeks").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const done = d.finalized.some((w) => w.week === d.last_week);
  const run = async () => {
    if (!window.confirm(`Finalize ${d.last_week}? This pays artist rewards and posts the top 10 to Discord.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/virtucity/charts/run", { week: d.last_week, post: true }); toast[data.ok ? "success" : "error"](data.ok ? `Finalized ${data.week}` : data.error); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface rounded-2xl p-5" data-testid="vc-chart-weeks-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-display font-semibold flex items-center gap-2"><BarChart3 size={16} className="text-gold" />Weekly City Chart</div>
        <Button onClick={run} disabled={busy || done} className="rounded-full btn-blue" data-testid="vc-chart-run-btn">{done ? `${d.last_week} finalized` : <><Send size={14} className="mr-1.5" />Finalize {d.last_week} now</>}</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Runs automatically every Monday 00:10 UTC. Last week preview: {d.last_week_preview.length ? d.last_week_preview.map((r) => `#${r.rank} ${r.title}`).slice(0, 5).join(" · ") : "no plays"}</p>
      {d.finalized.length > 0 && <div className="mt-3 space-y-1">{d.finalized.slice(0, 6).map((w) => (
        <div key={w.week} className="text-xs surface-2 rounded-lg px-3 py-1.5 flex justify-between" data-testid={`vc-week-${w.week}`}>
          <span className="font-mono">{w.week}</span><span className="text-muted-foreground">{(w.rows || []).length} songs · {(w.rewards || []).length} artists paid · {w.discord_message_id ? "posted" : "not posted"}</span>
        </div>))}</div>}
    </div>
  );
}

function PayoutsLogCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/virtucity/payouts").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="surface rounded-2xl p-5" data-testid="vc-payouts-log">
      <div className="font-display font-semibold mb-3">Recent city payouts</div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} className="text-xs surface-2 rounded-lg px-3 py-1.5 flex justify-between gap-2">
            <span className="truncate">@{r.user?.username} · {r.kind}{r.reason ? ` · ${r.reason}` : ""}</span>
            <span className={r.result.ok ? "text-emerald-400" : "text-rose-400"}>{r.result.ok ? `+${r.result.vc_credited} VC` : r.result.error}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
